import {useHistory} from "react-router-dom";
import {useEffect} from "react";
import {useFrontContext} from "v4/contexts/FrontContext";

export default function useHashPulse(isListingPage) {
    const {clearClickedId} = useFrontContext();
    const {location: {pathname, hash}, replace} = useHistory();

    useEffect(() => {
        if (!isListingPage || !hash) return;

        const id = hash.replace('#', '');
        let attempts = 0;
        const maxAttempts = 5;

        const cleanup = () => {
            replace(pathname);
            clearClickedId();
        };

        const tryPulse = () => {
            const element = document.getElementById(id);
            if (element) {
                requestAnimationFrame(() => {
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop - (window.innerHeight / 2) + (rect.height / 2);

                    const triggerPulse = () => {
                        pulseElement(element);
                        cleanup();
                    };

                    // If we are already at the target position, pulse immediately
                    if (Math.abs(window.scrollY - targetY) < 5) {
                        triggerPulse();
                    } else {
                        // Wait for smooth scroll to finish
                        const onScrollEnd = () => {
                            triggerPulse();
                            window.removeEventListener('scrollend', onScrollEnd);
                        };

                        window.addEventListener('scrollend', onScrollEnd);
                        window.scrollTo({ top: targetY, behavior: 'smooth' });

                        // Safety fallback for older browsers or interrupted scrolls
                        setTimeout(() => {
                            if (!element.classList.contains('pulse-bg')) {
                                onScrollEnd();
                            }
                        }, 1000);
                    }
                });
                return true;
            }
            return false;
        };

        if (!tryPulse()) {
            const interval = setInterval(() => {
                attempts++;
                if (tryPulse() || attempts >= maxAttempts) {
                    clearInterval(interval);
                    if (attempts >= maxAttempts) cleanup();
                }
            }, 200);
            return () => clearInterval(interval);
        }
    }, [isListingPage, pathname, hash, clearClickedId, replace]);
}

function pulseElement(element) {
    element.classList.add('pulse-bg');

    element.addEventListener('animationend', () => {
        element.classList.remove('pulse-bg');
    }, { once: true });
}
